import { Button } from "@/components/ui/button";
import { createClient } from "@/lib/client";
import { useUser } from "@/lib/useUser";
import { Compass } from "lucide-react";
import { Navigate } from "react-router";
import { useState } from "react";

const supabase = createClient();

const GoogleMark = () => (
  <svg viewBox="0 0 24 24" className="size-4">
    <path
      fill="#EA4335"
      d="M12 11v3.6h5.1c-.2 1.3-1.6 3.8-5.1 3.8-3.1 0-5.6-2.5-5.6-5.7s2.5-5.7 5.6-5.7c1.7 0 2.9.7 3.6 1.4l2.5-2.4C16.6 4.4 14.5 3.4 12 3.4 7 3.4 3 7.4 3 12.4S7 21.4 12 21.4c5.8 0 9.6-4.1 9.6-9.8 0-.7-.1-1.2-.2-1.7H12z"
    />
  </svg>
);

const GithubMark = () => (
  <svg viewBox="0 0 24 24" className="size-4 fill-current">
    <path d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.3.8-.6v-2c-3.2.7-3.9-1.5-3.9-1.5-.5-1.4-1.3-1.7-1.3-1.7-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.7 1.3 3.4 1 .1-.8.4-1.3.7-1.6-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.5-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.3 1.2 1-.3 2-.4 3-.4s2 .1 3 .4c2.3-1.5 3.3-1.2 3.3-1.2.7 1.6.2 2.8.1 3.1.7.8 1.2 1.8 1.2 3.1 0 4.4-2.7 5.4-5.3 5.7.4.4.8 1.1.8 2.2v3.2c0 .3.2.7.8.6 4.6-1.5 7.9-5.8 7.9-10.9 0-6.3-5.2-11.5-11.5-11.5z" />
  </svg>
);

function Auth() {
  const { user, loading } = useUser();
  const [busy, setBusy] = useState<"google" | "github" | null>(null);

  const login = async (provider: "google" | "github") => {
    setBusy(provider);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      console.error("oauth error:", error);
      setBusy(null);
    }
  };

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  return (
    <div className="grid min-h-screen w-full grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
      <section className="relative hidden overflow-hidden border-r border-border/60 lg:block">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 30% 20%, oklch(0.32 0.08 195 / 0.5) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 80% 90%, oklch(0.28 0.06 220 / 0.4) 0%, transparent 60%), oklch(0.14 0.005 240)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(oklch(0.78 0.13 200) 1px, transparent 1px), linear-gradient(90deg, oklch(0.78 0.13 200) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="relative flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <div className="grid size-9 place-items-center rounded-md bg-primary/15 ring-1 ring-primary/30">
              <Compass className="size-4 text-primary" strokeWidth={2.2} />
            </div>
            <span className="font-serif text-2xl italic tracking-tight">
              sanplexity
            </span>
          </div>

          <div className="max-w-xl">
            <h2 className="font-serif text-6xl leading-[0.95] tracking-tight">
              <span className="italic text-primary/90">Where</span>
              <br /> knowledge
              <br /> begins.
            </h2>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-muted-foreground">
              An answer engine that reads the web for you. Ask anything,
              receive sourced, structured replies — with the citations to
              prove it.
            </p>
          </div>

          <div className="flex items-center gap-6 text-[11px] tracking-[0.18em] text-muted-foreground/70 uppercase">
            <span>· grounded</span>
            <span>· cited</span>
            <span>· streaming</span>
          </div>
        </div>
      </section>

      <section className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-10 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="grid size-9 place-items-center rounded-md bg-primary/15 ring-1 ring-primary/30">
                <Compass className="size-4 text-primary" strokeWidth={2.2} />
              </div>
              <span className="font-serif text-2xl italic tracking-tight">
                sanplexity
              </span>
            </div>
          </div>

          <div className="mb-2 text-[11px] tracking-[0.2em] text-muted-foreground uppercase">
            sign in to continue
          </div>
          <h1 className="font-serif text-4xl tracking-tight">
            Welcome back.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Pick a provider — we'll keep your threads in sync.
          </p>

          <div className="mt-8 space-y-2.5">
            <Button
              onClick={() => login("google")}
              disabled={busy !== null}
              variant="outline"
              className="group h-12 w-full justify-start gap-3 rounded-xl border-border/70 bg-card/60 px-4 text-sm font-medium hover:border-primary/40 hover:bg-card"
            >
              <GoogleMark />
              <span className="flex-1 text-left">Continue with Google</span>
              <span className="text-muted-foreground/60 transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </Button>
            <Button
              onClick={() => login("github")}
              disabled={busy !== null}
              variant="outline"
              className="group h-12 w-full justify-start gap-3 rounded-xl border-border/70 bg-card/60 px-4 text-sm font-medium hover:border-primary/40 hover:bg-card"
            >
              <GithubMark />
              <span className="flex-1 text-left">Continue with GitHub</span>
              <span className="text-muted-foreground/60 transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </Button>
          </div>

          <p className="mt-10 text-[11px] leading-relaxed text-muted-foreground/70">
            By continuing, you agree to the terms and acknowledge sanplexity's
            answers are AI-generated and may need verification against the
            cited sources.
          </p>
        </div>
      </section>
    </div>
  );
}

export default Auth;
